import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, CloudUpload, Image as ImageIcon, Triangle, Info } from 'lucide-react';

const AboutAhamOverviewPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: ['', '', ''],
        content_data: {
            card_title: '',
            card_description: '',
            button_text: '',
            button_link: ''
        },
        image_url: '',
        is_active: true
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/about-aham/section/overview');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                const description = typeof data.description === 'string'
                    ? JSON.parse(data.description)
                    : (data.description || ['', '', '']);
                const content_data = typeof data.content_data === 'string'
                    ? JSON.parse(data.content_data)
                    : (data.content_data || { card_title: '', card_description: '', button_text: '', button_link: '' });

                setFormData({
                    title: data.title || '',
                    description: description,
                    content_data: content_data,
                    image_url: data.image_url || '',
                    is_active: data.is_active !== false
                });

                if (data.image_url) {
                    setImagePreview(`/${data.image_url}`);
                }
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching overview data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('card_')) {
            const field = name.replace('card_', '');
            setFormData(prev => ({
                ...prev,
                content_data: {
                    ...prev.content_data,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleDescriptionChange = (index, value) => {
        const newDescription = [...formData.description];
        newDescription[index] = value;
        setFormData(prev => ({ ...prev, description: newDescription }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const submitData = new FormData();
            submitData.append('title', formData.title);
            submitData.append('description', JSON.stringify(formData.description));
            submitData.append('content_data', JSON.stringify(formData.content_data));
            submitData.append('is_active', formData.is_active ? '1' : '0');
            submitData.append('image_url', formData.image_url);

            if (imageFile) {
                submitData.append('image', imageFile);
            }

            const response = await axios.put('/api/admin/about-aham/section/overview', submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                toast.success('Overview updated successfully');
                fetchData();
            }
        } catch (error) {
            console.error('Error updating overview:', error);
            toast.error('Failed to update overview');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">About Aham - Overview</h1>
                    <p className="text-slate-500 mt-1">Manage the "About Aham Housing Finance Limited" section</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Main Content */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <h2 className="text-lg font-bold text-slate-800 border-b pb-2">Main Content</h2>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. About Aham Housing Finance Limited"
                            required
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Description Paragraphs</label>
                        {formData.description.map((para, index) => (
                            <div key={index}>
                                <label className="block text-xs text-slate-500 mb-1">Paragraph {index + 1}</label>
                                <textarea
                                    value={para}
                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                                    placeholder={`Paragraph ${index + 1}...`}
                                    required
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Card Content */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <h2 className="text-lg font-bold text-slate-800 border-b pb-2">Side Card Content</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Card Title</label>
                            <input
                                type="text"
                                name="card_card_title"
                                value={formData.content_data.card_title}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                placeholder="e.g. Empowering Home Dreams"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Button Text</label>
                            <input
                                type="text"
                                name="card_button_text"
                                value={formData.content_data.button_text}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                placeholder="e.g. Contact Us"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Card Description</label>
                        <textarea
                            name="card_card_description"
                            value={formData.content_data.card_description}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                            placeholder="Card description..."
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Button Link</label>
                        <input
                            type="text"
                            name="card_button_link"
                            value={formData.content_data.button_link}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. /contact"
                            required
                        />
                    </div>
                </div>

                {/* Image Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <div className="space-y-1">
                        <h2 className="text-lg font-bold text-slate-800">Banner Image (Optional)</h2>
                        <div className="flex items-center gap-2 text-slate-500">
                            <Triangle className="h-4 w-4 fill-slate-200" />
                            <p className="text-sm">Recommended: 1600 × 800 pixels | Formats: WebP, PNG only</p>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6 items-start pt-2">
                        <div className="flex-1 w-full">
                            <label className="block border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                                <CloudUpload className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                                <p className="text-lg font-semibold text-slate-600">Change Image</p>
                                <p className="text-xs text-slate-400 mt-1">WebP or PNG, max 10MB</p>
                            </label>
                        </div>

                        <div className="w-full md:w-72 space-y-2">
                            <p className="text-sm font-semibold text-slate-700">Preview</p>
                            <div className="aspect-[1600/800] rounded-xl overflow-hidden border border-slate-200 shadow-sm relative group bg-slate-50">
                                {imagePreview ? (
                                    <>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <ImageIcon className="text-white h-8 w-8" />
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <ImageIcon className="text-slate-200 h-12 w-12" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-lg shadow-primary-500/30 transition-all font-bold text-lg"
                    >
                        <Save size={20} />
                        {saving ? 'Saving Changes...' : 'Save All Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AboutAhamOverviewPage;
